/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { ChartType, VisualChart } from "src/gql/visuals/types";
import { ChartScale } from "./scales";
import { QueryResultColumn } from "src/utils/results/types";

export const chartOptionColor = (visual: VisualChart, name: string) => {
  return chartOptionObject(visual, name)?.color;
};

export const chartOptionVisible = (visual: VisualChart, name: string) => {
  return chartOptionObject(visual, name)?.visible !== false;
};

export const chartOptionName = (visual: VisualChart, name: string) => {
  return visual.options.seriesOptions?.[name]?.name || name;
};

export const chartOptionPercentages = (visual: VisualChart) => {
  return Boolean(visual.options.series?.percentValues);
};

export const chartOptionLabels = (visual: VisualChart) => {
  return Boolean(visual.options.showDataLabels);
};

export const chartOptionLegend = (visual: VisualChart) => {
  return Boolean(visual.options.legend?.enabled);
};

export const chartOptionStack = (visual: VisualChart) => {
  return (
    visual.options.globalSeriesType !== "line" &&
    Boolean(visual.options.series?.stacking)
  );
};

export const chartOptionShowTotal = (visual: VisualChart) => {
  return Boolean(visual.options.series?.showTotal);
};

export const chartOptionSomeColumnCharts = (visual: VisualChart) => {
  const seriesOptions = Object.values(visual.options.seriesOptions ?? {});
  return (
    seriesOptions.some((s) => s.type === "column") ||
    visual.options.globalSeriesType === "column"
  );
};

const chartOptionOnly = (type: ChartType) => (visual: VisualChart) => {
  const seriesOptions = Object.values(visual.options.seriesOptions ?? {});
  if (seriesOptions.length > 0) {
    return seriesOptions.every((s) => s.type === type);
  } else {
    return visual.options.globalSeriesType === type;
  }
};

// The chart and all the chart series are 'pie' type
export const chartOptionOnlyPieCharts = chartOptionOnly("pie");

// The chart and all the chart series are 'scatter' type
export const chartOptionOnlyScatterCharts = chartOptionOnly("scatter");

/**
 * Function checks if the current chart is a Bubble chart.
 * In some places we don't have access to ChartScale or QueryResultColumn, therefore we support both options
 * @param visual
 * @param zData z-axis ChartScale or QueryResultColumn
 */
export const chartOptionBubbleChart = (
  visual: VisualChart,
  zData?: ChartScale | QueryResultColumn
): zData is ChartScale | QueryResultColumn => {
  return (
    visual.options.globalSeriesType === "scatter" &&
    typeof zData !== "undefined"
  );
};

const chartOptionObject = (visual: VisualChart, name: string) => {
  const { valuesOptions, seriesOptions } = visual.options;
  return valuesOptions?.[name] ?? seriesOptions?.[name];
};
